/**
 * Compact summary of order context + breach tag for the ticket detail page.
 */
import { Banknote, ChevronRight, MapPin, Phone, Receipt, Store as StoreIcon, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { StaffOrderTimeline } from '@/components/orders/StaffOrderTimeline';
import { fmt } from '@/lib/format';
import { statusLabel, statusTone } from '@/lib/status';
import type { CrmTicket } from '@/api/bi/crm';
import { slaTagLabel } from '@/lib/slaTags';
import { notify } from '@/lib/toast';
import { Button } from '../ui/Button';

export function TicketSummary({ ticket }: { ticket: CrmTicket }) {
  const order = ticket.order ?? null;

    async function copyTransactionCode() {
      try {
        await navigator.clipboard.writeText(ticket.order?.transaction_code || "");
        notify.success('Transaction code copied.');
      } catch {
        notify.error('Could not copy the code.');
      }
    }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Receipt className="h-4 w-4" />  Linked order
        </CardTitle>
        {order ? (
          <Link
            to={`/orders/${order.id}`}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            View full order <ChevronRight className="inline h-3 w-3" />
          </Link>
        ) : null}
      </CardHeader>
      <CardBody className="space-y-3">
        {order ? (
          <>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium">#{order.id}</span>
              <Badge tone={statusTone(order.status)}>{statusLabel(order.status)}</Badge>
              <span className="ml-auto kpi-num text-base">{fmt.npr(order.final_total)}</span>
            </div>
            <div className="grid grid-cols-1 gap-1.5 text-[11px] text-muted-foreground sm:grid-cols-2">
              <Row icon={<StoreIcon className="h-3 w-3" />} label={order.store_name ?? '—'} />
              <Row
                icon={<User className="h-3 w-3" />}
                label={order.customer?.name ?? 'No customer snapshot'}
              />
              {order.customer?.phone ? (
                <Row icon={<Phone className="h-3 w-3" />} label={order.customer.phone} />
              ) : null}
              {order.customer?.address ? (
                <Row icon={<MapPin className="h-3 w-3" />} label={order.customer.address} />
              ) : null}

                {order.transaction_code ? (
                 <div className='flex items-center justify-between w-fit'> 
                    {order.payment_type || ""}:
                    <Button variant="ghost" size="sm" onClick={copyTransactionCode} className="gap-1 w-fit">
                                     <Banknote className="h-3.5 w-3.5" /> {order.transaction_code}
                                </Button>
                 </div>

              
              ) : null}
           
            </div>
            <StaffOrderTimeline
              order={{
                created_timestamp: order.created_timestamp,
                packed_timestamp: order.packed_timestamp,
                billed_timestamp: order.billed_timestamp,
                picked_timestamp: order.picked_timestamp,
                reached_timestamp: order.reached_timestamp,
                completed_timestamp: order.completed_timestamp,
                return_timestamp: order.return_timestamp ?? null,
                cancelled_timestamp: order.cancelled_timestamp,
                cancellation_reason: order.cancellation_reason,
                payment_type: order.payment_type,
                is_payment_done: order.is_payment_done,
              }}
            />
          </>
        ) : (
          <div className="text-xs text-muted-foreground">
            This ticket is not attached to an order.
          </div>
        )}

        {ticket.breach_tag ? (
          <div className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs">
            <div className="font-medium text-warning-soft">
              SLA breach: {slaTagLabel(ticket.breach_tag.tag)}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Late by {fmt.duration(ticket.breach_tag.late_duration ?? 0)}
              {' · '}
              detected {fmt.datetime(ticket.breach_tag.created_at)}
            </div>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function Row({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 truncate">
      {icon}
      <span className="truncate text-foreground/80">{label}</span>
    </span>
  );
}

